import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Mic, CheckCircle2, Sparkles, Building2, ArrowRight, Play, Pause,
  Volume2, Users, Crown, Star, Globe, Shield,
} from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/apiConfig';

/**
 * PublicPricingPage
 * ──────────────────
 * Public marketing page at /preise-b2b that surfaces the seeded b2b_plans
 * (Basic / Pro / Enterprise) and routes interested visitors straight into
 * the voice-first onboarding wizard (Iter 107).
 *
 * Key UX hooks:
 *   • Plan-card click → /orb-chat?wizard=b2b&plan=<key>  (OrbAgent reads
 *     URL params and seeds the first message that triggers the wizard).
 *   • CTA "Hey Orb" → same route with explicit `auto=1` flag so OrbAgent
 *     auto-sends the seeded message and starts the multi-turn flow.
 */
const ICON_MAP = {
  basic:      { icon: Users,       gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
  pro:        { icon: Star,        gradient: 'linear-gradient(135deg,#f59e0b,#ec4899)' },
  enterprise: { icon: Crown,       gradient: 'linear-gradient(135deg,#a855f7,#22d3ee)' },
};

const POPULAR_KEY = 'pro';

export default function PublicPricingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/b2b/plans`);
        if (!cancelled) setPlans(res.data?.plans || []);
      } catch (e) {
        // graceful fallback to hardcoded shape if API down — still rendered
        if (!cancelled) setPlans([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startVoiceFlow = (planKey) => {
    const params = new URLSearchParams({
      wizard: 'b2b',
      plan: planKey || 'pro',
      auto: '1',
    });
    navigate(`/orb-chat?${params.toString()}`);
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setPlaying(true);
    }
  };

  return (
    <div data-testid="public-pricing-page" style={styles.page} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section style={styles.hero}>
        <div style={styles.heroBadge} data-testid="public-pricing-badge">
          <Sparkles size={14} />
          <span>{t('pricing.heroBadge', '90-Sekunden-Setup per Sprache')}</span>
        </div>
        <h1 style={styles.heroTitle} data-testid="public-pricing-title">
          {t('pricing.heroTitle', 'B2B-Mobilität, die')}
          <span style={styles.heroAccent}>
            {' '}{t('pricing.heroTitleAccent', 'spricht statt klickt.')}
          </span>
        </h1>
        <p style={styles.heroSub}>
          {t('pricing.heroSub',
             'Drei Pläne, eine Sprachbefehl-Anmeldung. Skaliere dein Team-Fahrtkonto in unter 90 Sekunden — ohne ein einziges Formular.')}
        </p>

        <button
          data-testid="public-pricing-hero-cta"
          style={styles.heroCta}
          onClick={() => startVoiceFlow(POPULAR_KEY)}
        >
          <Mic size={18} />
          <span>{t('pricing.heroCta', 'Direkt per Sprache anmelden — Sag „Hey Orb"')}</span>
          <ArrowRight size={18} />
        </button>

        {/* trust strip */}
        <div style={styles.trustStrip} data-testid="public-pricing-trust">
          <span><Globe size={13} /> {t('pricing.trust1', '10 Sprachen live')}</span>
          <span><Shield size={13} /> {t('pricing.trust2', 'DSGVO-konform · Stripe Billing')}</span>
          <span><Sparkles size={13} /> {t('pricing.trust3', 'Voice-AI Claude Sonnet 4.5')}</span>
        </div>
      </section>

      {/* ── AUDIO DEMO ───────────────────────────────────────── */}
      <section style={styles.audioSection} data-testid="public-pricing-audio">
        <div style={styles.audioHeader}>
          <Volume2 size={16} color="#a855f7" />
          <span style={styles.audioLabel}>
            {t('pricing.audioLabel', '90-Sekunden-Demo anhören')}
          </span>
        </div>
        <div style={styles.audioPlayer}>
          <button
            data-testid="public-pricing-audio-play"
            onClick={toggleAudio}
            style={{
              ...styles.audioBtn,
              background: playing
                ? 'linear-gradient(135deg,#ef4444,#f97316)'
                : 'linear-gradient(135deg,#a855f7,#6366f1)',
            }}
            aria-label={playing ? 'Pause demo audio' : 'Play demo audio'}
          >
            {playing ? <Pause size={20} /> : <Play size={20} />}
          </button>
          <div style={styles.audioMeta}>
            <div style={styles.audioTitle}>
              {t('pricing.audioTitle', '„Hey Orb, meld unsere Firma an"')}
            </div>
            <div style={styles.audioSubtitle}>
              {t('pricing.audioSubtitle',
                 'Live-Mitschnitt: Org-Admin → Voice-Wizard → Stripe-Checkout in 87 Sekunden.')}
            </div>
          </div>
          {/* HTML5 audio — silent if file missing, no error overlay */}
          <audio
            ref={audioRef}
            src="/audio/orb-b2b-demo.mp3"
            onEnded={() => setPlaying(false)}
            onError={() => setPlaying(false)}
            preload="none"
            data-testid="public-pricing-audio-el"
          />
        </div>
      </section>

      {/* ── PLAN CARDS ──────────────────────────────────────── */}
      <section style={styles.cardsSection}>
        <h2 style={styles.sectionTitle}>
          {t('pricing.plansTitle', 'Wähle deinen Plan')}
        </h2>
        {loading ? (
          <div style={styles.skeletonRow}>
            <div style={styles.skeletonCard} />
            <div style={styles.skeletonCard} />
            <div style={styles.skeletonCard} />
          </div>
        ) : (
          <div style={styles.cardsGrid} data-testid="public-pricing-cards">
            {plans.map((plan) => {
              const meta = ICON_MAP[plan.plan_key] || ICON_MAP.basic;
              const Icon = meta.icon;
              const isPopular = plan.plan_key === POPULAR_KEY;
              return (
                <div
                  key={plan.plan_key}
                  data-testid={`public-pricing-card-${plan.plan_key}`}
                  style={{
                    ...styles.card,
                    ...(isPopular ? styles.cardPopular : {}),
                  }}
                >
                  {isPopular && (
                    <div style={styles.popularBadge} data-testid="public-pricing-popular-badge">
                      <Sparkles size={12} />
                      {t('pricing.popular', 'Beliebt')}
                    </div>
                  )}
                  <div style={{ ...styles.iconWrap, background: meta.gradient }}>
                    <Icon size={22} color="#fff" />
                  </div>
                  <div style={styles.planName}>{plan.name}</div>
                  <div style={styles.priceRow}>
                    <span style={styles.priceAmount}>
                      {Number(plan.seat_price_eur).toFixed(0)}
                    </span>
                    <span style={styles.priceCurrency}>€</span>
                    <span style={styles.pricePeriod}>
                      / {t('pricing.seatMonth', 'Sitz/Monat')}
                    </span>
                  </div>
                  <div style={styles.planMeta}>
                    {t('pricing.includedSeats', '{{n}} Sitze inkludiert', { n: plan.included_seats })}
                    {plan.max_seats
                      ? ` · ${t('pricing.maxSeats', 'max. {{n}}', { n: plan.max_seats })}`
                      : ` · ${t('pricing.unlimitedSeats', 'unbegrenzt skalierbar')}`}
                  </div>
                  <ul style={styles.features}>
                    {renderFeatures(plan.features || {}, t).map((feat, i) => (
                      <li key={i} style={styles.featureItem}>
                        <CheckCircle2 size={14} color="#22c55e" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`public-pricing-card-cta-${plan.plan_key}`}
                    onClick={() => startVoiceFlow(plan.plan_key)}
                    style={{
                      ...styles.cardCta,
                      ...(isPopular ? styles.cardCtaPopular : {}),
                    }}
                  >
                    <Mic size={15} />
                    {t('pricing.cardCta', 'Sag „Hey Orb" für {{plan}}', { plan: plan.name })}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── FOOTER PITCH ────────────────────────────────────── */}
      <section style={styles.footerPitch}>
        <Building2 size={28} color="#a855f7" />
        <div>
          <h3 style={styles.footerTitle}>
            {t('pricing.footerTitle', 'Setup unter 90 Sekunden. Garantiert.')}
          </h3>
          <p style={styles.footerSub}>
            {t('pricing.footerSub',
               'Sprich einen Satz auf Deutsch, Englisch, Französisch oder einer von 7 weiteren Sprachen — der Orb-Agent erkennt Firma, Plan und Sitzanzahl, legt die Subscription an und schickt dir den Stripe-Checkout direkt aufs Handy.')}
          </p>
          <button
            data-testid="public-pricing-footer-cta"
            style={styles.footerCta}
            onClick={() => startVoiceFlow(POPULAR_KEY)}
          >
            {t('pricing.footerCta', 'Jetzt per Sprache starten')}
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <style>{`
        @keyframes pricingPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(168,85,247,0.55); }
          50%      { box-shadow: 0 0 0 14px rgba(168,85,247,0); }
        }
        @keyframes float-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ── Feature list builder (NO hardcoded English bleed) ─────────────
function renderFeatures(featuresJsonb, t) {
  const lines = [];
  // priorities: face_match_override, sos_dashboard, priority_match, analytics, deepl, custom_branding, dedicated_csm
  if (featuresJsonb.face_match_override) lines.push(t('pricing.featureFaceOverride', 'Face-Match Override für Admins'));
  if (featuresJsonb.sos_dashboard)       lines.push(t('pricing.featureSos',          'SOS-Dashboard Realtime'));
  if (featuresJsonb.priority_match)      lines.push(t('pricing.featurePriority',     'Priority Matching für Mitarbeiter'));
  if (featuresJsonb.analytics === 'full') lines.push(t('pricing.featureAnalyticsFull','Full Analytics & Insights'));
  else if (featuresJsonb.analytics === 'basic') lines.push(t('pricing.featureAnalyticsBasic', 'Basis-Analytics'));
  if (featuresJsonb.deepl)               lines.push(t('pricing.featureDeepl',        'Echtzeit-Übersetzung 10 Sprachen'));
  if (featuresJsonb.custom_branding)     lines.push(t('pricing.featureBranding',     'Custom Branding & White-Label'));
  if (featuresJsonb.dedicated_csm)       lines.push(t('pricing.featureCsm',          'Dedicated Customer Success Manager'));
  if (lines.length === 0) lines.push(t('pricing.featureFallback', 'Alle Kern-Funktionen inklusive'));
  return lines;
}

// ── Styles (Design-System konform) ────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh', color: '#e2e8f0',
    background: 'radial-gradient(circle at 20% -10%, #312e81 0%, #0b1220 55%, #020617 100%)',
    fontFamily: '"Inter","Segoe UI",system-ui,sans-serif',
    paddingBottom: 60,
  },

  // Hero
  hero: { padding: '64px 24px 48px', maxWidth: 980, margin: '0 auto', textAlign: 'center',
          animation: 'float-in .7s ease-out both' },
  heroBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'rgba(168,85,247,0.12)', color: '#d8b4fe',
    border: '1px solid rgba(168,85,247,0.3)', padding: '6px 14px',
    borderRadius: 999, fontSize: 12, fontWeight: 600,
    marginBottom: 18, letterSpacing: 0.3,
  },
  heroTitle: { fontSize: 'clamp(34px, 5vw, 58px)', lineHeight: 1.08, margin: 0,
               fontWeight: 800, letterSpacing: -0.5, color: '#f8fafc' },
  heroAccent: {
    background: 'linear-gradient(135deg,#a855f7,#22d3ee,#22c55e)',
    backgroundClip: 'text', WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  heroSub: { fontSize: 16, color: '#94a3b8', marginTop: 18, maxWidth: 640,
             margin: '18px auto 28px', lineHeight: 1.6 },
  heroCta: {
    display: 'inline-flex', alignItems: 'center', gap: 10,
    background: 'linear-gradient(135deg,#a855f7,#6366f1)',
    color: '#fff', border: 'none', padding: '15px 32px',
    borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
    animation: 'pricingPulse 2.4s infinite',
    boxShadow: '0 12px 36px rgba(168,85,247,0.4)',
  },
  trustStrip: {
    display: 'flex', justifyContent: 'center', gap: 26, flexWrap: 'wrap',
    marginTop: 28, color: '#94a3b8', fontSize: 12,
  },

  // Audio Demo
  audioSection: { maxWidth: 720, margin: '24px auto 56px', padding: '0 24px' },
  audioHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 },
  audioLabel: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.8, color: '#cbd5e1' },
  audioPlayer: {
    display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px',
    background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(20px)',
    border: '1px solid rgba(168,85,247,0.25)', borderRadius: 16,
  },
  audioBtn: {
    width: 52, height: 52, borderRadius: '50%', border: 'none',
    color: '#fff', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
  },
  audioMeta: { flex: 1 },
  audioTitle: { fontSize: 14, fontWeight: 700, color: '#f1f5f9' },
  audioSubtitle: { fontSize: 12, color: '#94a3b8', marginTop: 2 },

  // Cards
  cardsSection: { maxWidth: 1200, margin: '0 auto', padding: '20px 24px' },
  sectionTitle: { fontSize: 26, fontWeight: 700, textAlign: 'center',
                  margin: '0 0 36px', color: '#f1f5f9' },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
               gap: 22, alignItems: 'stretch' },
  card: {
    position: 'relative', background: 'rgba(15,23,42,0.65)',
    backdropFilter: 'blur(24px)', border: '1px solid rgba(148,163,184,0.18)',
    borderRadius: 22, padding: 28, display: 'flex', flexDirection: 'column',
    gap: 14, transition: 'transform .25s, border-color .25s',
    animation: 'float-in .8s ease-out both',
  },
  cardPopular: {
    border: '1px solid rgba(245,158,11,0.55)',
    boxShadow: '0 16px 48px rgba(245,158,11,0.18)',
    transform: 'translateY(-6px)',
  },
  popularBadge: {
    position: 'absolute', top: -12, right: 18,
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: 'linear-gradient(135deg,#f59e0b,#ec4899)',
    color: '#fff', padding: '4px 12px', borderRadius: 999,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
  },
  iconWrap: {
    width: 52, height: 52, borderRadius: 14, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  planName: { fontSize: 22, fontWeight: 700, color: '#fff' },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: 4 },
  priceAmount: { fontSize: 44, fontWeight: 800, color: '#fff', lineHeight: 1 },
  priceCurrency: { fontSize: 22, fontWeight: 700, color: '#fff', marginLeft: 2 },
  pricePeriod: { fontSize: 13, color: '#94a3b8', marginLeft: 6 },
  planMeta: { fontSize: 12, color: '#94a3b8', marginTop: -8 },
  features: { listStyle: 'none', padding: 0, margin: '4px 0 0',
              display: 'flex', flexDirection: 'column', gap: 8 },
  featureItem: { display: 'flex', alignItems: 'flex-start', gap: 8,
                 fontSize: 13.5, color: '#cbd5e1', lineHeight: 1.45 },
  cardCta: {
    marginTop: 'auto', display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', gap: 8, background: 'rgba(255,255,255,0.06)',
    color: '#e9d5ff', border: '1px solid rgba(168,85,247,0.4)',
    padding: '11px 16px', borderRadius: 12, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, transition: 'all .2s',
  },
  cardCtaPopular: {
    background: 'linear-gradient(135deg,#f59e0b,#ec4899)',
    color: '#fff', border: 'none',
    boxShadow: '0 8px 22px rgba(245,158,11,0.4)',
  },

  // Skeleton
  skeletonRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
                 gap: 22 },
  skeletonCard: {
    height: 320, borderRadius: 22, background:
      'linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.6s infinite linear',
  },

  // Footer pitch
  footerPitch: {
    display: 'flex', gap: 22, alignItems: 'flex-start',
    maxWidth: 980, margin: '70px auto 0', padding: '32px 24px',
    background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(34,211,238,0.06))',
    border: '1px solid rgba(168,85,247,0.22)', borderRadius: 22,
  },
  footerTitle: { fontSize: 22, margin: 0, color: '#f1f5f9' },
  footerSub: { fontSize: 14, color: '#94a3b8', marginTop: 8, lineHeight: 1.55 },
  footerCta: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(168,85,247,0.18)', color: '#e9d5ff',
    border: '1px solid rgba(168,85,247,0.4)', padding: '10px 18px',
    borderRadius: 10, marginTop: 14, cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
  },
};
